import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onSearch: (value: string) => void;
  debounceMs?: number;
  className?: string;
  showClearButton?: boolean;
}

function SearchInput({
  onSearch,
  debounceMs = 300,
  className,
  showClearButton = true,
  value: propValue,
  onChange: propOnChange,
  ...props
}: SearchInputProps) {
  const [searchTerm, setSearchTerm] = useState(propValue?.toString() || '');

  // Handle controlled input value
  useEffect(() => {
    if (propValue !== undefined) {
      setSearchTerm(propValue.toString());
    }
  }, [propValue]);

  // Handle search term changes with debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      onSearch(searchTerm);
    }, debounceMs);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debounceMs, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (propOnChange) {
      propOnChange(e);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  return (
    <div className={cn('relative', className)}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text" // Changed from search to text to prevent browser search behavior
        className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-800 dark:text-gray-200"
        placeholder="Pesquisar..."
        value={searchTerm}
        onChange={handleChange}
        {...props}
      />
      {showClearButton && searchTerm && (
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={handleClear}
          tabIndex={-1}
          aria-label="Limpar pesquisa"
        >
          <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
        </button>
      )}
    </div>
  );
}

export default SearchInput;